import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { featureFlagsApi } from '@/api/featureFlags';

// Feature flags that are ON by default — shown unless the backend explicitly disables them
const DEFAULT_ENABLED = new Set<string>();

const FLAGS_CACHE_KEY = 'mentara.enabledFeatures';

/** Last-known flags from this browser tab's session. Guarded: storage can be
 *  blocked/full/absent (private mode, prerender), and a corrupt value is ignored. */
export function readCachedFeatures(): string[] | null {
  try {
    const raw = sessionStorage.getItem(FLAGS_CACHE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((f) => typeof f === 'string') ? parsed : null;
  } catch {
    return null;
  }
}

function writeCachedFeatures(features: string[]): void {
  try {
    sessionStorage.setItem(FLAGS_CACHE_KEY, JSON.stringify(features));
  } catch {
    /* best-effort */
  }
}

// Seeded synchronously from the session cache so the first paint already has
// last-known flags — the network fetch only refreshes them.
const _cachedAtBoot = readCachedFeatures();
let _enabledFeatures = new Set<string>(_cachedAtBoot ?? DEFAULT_ENABLED);

export type FeatureRef = { name: string; isActive: () => boolean };

function featureFlag(name: string): FeatureRef {
  return { name, isActive: () => _enabledFeatures.has(name) } as const;
}

export const Feature = {
  ORGANIZATIONS: featureFlag('ORGANIZATIONS'),
  EMAIL: featureFlag('EMAIL'),
  PHONE_NUMBER: featureFlag('PHONE_NUMBER'),
  PROFILE_PICTURE: featureFlag('PROFILE_PICTURE'),
  AI_QUESTION_GENERATION: featureFlag('AI_QUESTION_GENERATION'),
  // Gates the AI assistant for admins only.
  AI_ASSISTANT: featureFlag('AI_ASSISTANT'),
  // General availability: gates the AI assistant for non-admin roles.
  AI_ASSISTANT_GA: featureFlag('AI_ASSISTANT_GA'),
  QUICK_QUESTION_ACCESS: featureFlag('QUICK_QUESTION_ACCESS'),
  SAT_BLUEBOOK_UI: featureFlag('SAT_BLUEBOOK_UI'),
  SAT_REINFORCE_PRACTICE: featureFlag('SAT_REINFORCE_PRACTICE'),
  SAT_BLUEBOOK_INTAKE: featureFlag('SAT_BLUEBOOK_INTAKE'),
} as const;

/** AI_ASSISTANT gates access for admins; AI_ASSISTANT_GA gates access for everyone else. */
export function isAiAssistantEnabled(role: string | undefined, isEnabled: (feature: string) => boolean): boolean {
  return role === 'ADMIN' ? isEnabled(Feature.AI_ASSISTANT.name) : isEnabled(Feature.AI_ASSISTANT_GA.name);
}

interface FeaturesContextValue {
  features: Set<string>;
  isEnabled: (feature: string) => boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

const FeaturesContext = createContext<FeaturesContextValue>({
  features: new Set(),
  isEnabled: () => false,
  loading: true,
  refresh: async () => {},
});

export function FeaturesProvider({ children }: { children: ReactNode }) {
  const [features, setFeatures] = useState<Set<string>>(() => new Set(_enabledFeatures));
  // With last-known flags in hand nothing needs to wait on the network.
  const [loading, setLoading] = useState(_cachedAtBoot === null);

  const refresh = async () => {
    // During prerendering, feature flags are injected directly into the window
    const injected = (window as unknown as { __PRERENDER_INJECTED?: { features?: string[] } }).__PRERENDER_INJECTED;
    if (injected?.features) {
      const set = new Set(injected.features);
      setFeatures(set);
      _enabledFeatures = set;
      setLoading(false);
      return;
    }

    try {
      const enabled = await featureFlagsApi.getEnabled();
      const set = new Set(enabled);
      setFeatures(set);
      _enabledFeatures = set;
      writeCachedFeatures(enabled);
    } catch {
      console.warn('Failed to fetch feature flags, using last-known flags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const isEnabled = (feature: string) => features.has(feature);

  return (
    <FeaturesContext.Provider value={{ features, isEnabled, loading, refresh }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures() {
  return useContext(FeaturesContext);
}
